import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Sparkles, ArrowRight, Wheat, Truck, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_BG = "/images/webp/fon.webp";

const PRESET_CATEGORIES_ORDER = [
  "Сытные",
  "Сладкие",
  "Пирожки печеные",
  "Пирожки жареные",
  "Фаст фуд",
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("Все");

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    const present = PRESET_CATEGORIES_ORDER.filter((c) => set.has(c));
    const extras = [...set].filter((c) => !PRESET_CATEGORIES_ORDER.includes(c));
    return ["Все", ...present, ...extras];
  }, [products]);

  const filtered = useMemo(() => {
    if (active === "Все") return products;
    return products.filter((p) => p.category === active);
  }, [products, active]);

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Ишеевские пироги — доставка домашних пирогов в Ульяновске | pirogi73.ru</title>
        <meta name="description" content="Домашние пироги и пирожки с доставкой по Ульяновску и области. Закажите мясной, капустный, яблочный или рыбный пирог онлайн. Работаем без выходных." />
        <link rel="canonical" href="https://pirogi73.ru/" />
        <meta property="og:title" content="Ишеевские пироги — Ульяновск" />
        <meta property="og:description" content="Домашние пироги с доставкой по Ульяновску и области. Каталог мясных, капустных, яблочных и рыбных пирогов." />
        <meta property="og:image" content="https://pirogi73.ru/images/webp/fon.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pirogi73.ru/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://pirogi73.ru/#business",
          "name": "Ишеевские пироги",
          "telephone": "+79378764537",
          "url": "https://pirogi73.ru",
          "image": "https://pirogi73.ru/images/webp/fon.webp",
          "servesCuisine": "Русская выпечка",
          "foundingDate": "1995",
          "address": { "@type": "PostalAddress", "addressLocality": "Ульяновск", "addressRegion": "Ульяновская область", "addressCountry": "RU" },
          "areaServed": { "@type": "City", "name": "Ульяновск" },
          "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "09:00", "closes": "21:00" }
        })}</script>
      </Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1410]/85 via-[#1a1410]/55 to-[#1a1410]/25"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F9F6F0]"></div>
          {/* Animated steam over the pie */}
          <div className="steam" aria-hidden="true" data-testid="hero-steam">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-44">
          <div className="max-w-2xl reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-xs tracking-widest uppercase text-[#F9F6F0] mb-8">
              <Sparkles size={12} className="text-[#E8B86A]" />
              Домашняя пекарня · Ульяновск
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#F9F6F0] mb-6 drop-shadow-lg">
              ПИРОГИ & ПИРОЖКИ{" "}
              <span className="italic text-[#E8B86A]">Ишеевские</span>
              
            </h1>
            <p className="text-lg text-[#F2EBE1]/90 leading-relaxed mb-10 max-w-xl">
              Собственное производство
              <br></br>
              На заказ с доставкой. <br></br> 
              По г. Ульяновску и области. 
              <br></br> 
              Традиционные рецепты и только свежие продукты.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#catalog" className="btn-primary" data-testid="hero-cta-catalog">
                Выбрать пирог
                <ArrowRight size={16} />
              </a>
              <Link
                to="/dostavka-pirogov-ulyanovsk/"
                className="inline-flex items-center px-6 py-3 rounded-full border border-white/40 text-[#F9F6F0] hover:bg-white/10 transition-colors"
                data-testid="hero-cta-delivery"
              >
                Условия доставки
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE FACTS */}
      <section className="bg-[#2C2520] text-[#F2EBE1] overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-6 gap-16">
          {Array.from({ length: 2 }).map((_, ri) =>
            ["Свежее тесто каждое утро", "Бесплатная доставка от 3000 ₽", "Без выходных", "Щедрая начинка", "Традиционные рецепты", "Заказ от 1500 ₽"].map(
              (t, i) => (
                <span
                  key={`${ri}-${i}`}
                  className="font-display text-2xl flex items-center gap-16"
                >
                  {t}
                  <span className="text-[#C65D3B]">✦</span>
                </span>
              ),
            ),
          )}
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-12 min-w-0">
            <div className="label-eyebrow mb-4">Почему мы</div>
            <h2 className="why-heading font-display text-3xl sm:text-5xl text-[#2C2520] leading-tight">
              Мы храним традиции русской пекарни уже многие годы. Каждый пирог, как для себя. Печем с душой, доставляем с заботой.
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Wheat,
              title: "Свежее тесто",
              text: "Замешиваем каждое утро только из свежих ингредиентов по проверенным рецептам.",
            },
            {
              icon: Heart,
              title: "Щедрая начинка",
              text: "Не экономим на начинке: каждый пирог получается сочным и сытным.",
            },
            {
              icon: Truck,
              title: "Надёжная доставка",
              text: "Привезём тёплыми по Ульяновску и области. От 3000 ₽ доставка по городу бесплатно.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="bg-white border border-[#E8E3DA] rounded-[24px] p-8 reveal card-hover"
              style={{ animationDelay: `${i * 0.08}s` }}
              data-testid={`feature-${i}`}
            >
              <div className="w-14 h-14 rounded-full bg-[#F2EBE1] text-[#C65D3B] flex items-center justify-center mb-6">
                <f.icon size={24} />
              </div>
              <h3 className="font-display text-2xl text-[#2C2520] mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-[#5C4F45] leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATALOG */}
      <section
        id="catalog"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24"
        data-testid="catalog-section"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="label-eyebrow mb-3">Наш каталог</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[#2C2520]">
              Пироги, пирожки & фастфуд
            </h2>
          </div>
        </div>

        <div className="sticky top-20 z-30 bg-[#F9F6F0] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-7" data-testid="category-filters-wrap">
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`chip ${active === c ? "active" : ""}`}
              data-testid={`filter-${c.replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-[#E8E3DA] rounded-[24px] overflow-hidden animate-pulse"
              >
                <div className="aspect-[4/3] bg-[#F2EBE1]" />
                <div className="p-6 space-y-3">
                  <div className="h-6 bg-[#F2EBE1] rounded w-2/3" />
                  <div className="h-4 bg-[#F2EBE1] rounded w-full" />
                  <div className="h-4 bg-[#F2EBE1] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#5C4F45]">
            В этой категории пока нет товаров.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
